import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Typography from "components/basic/typography";
import CarChargerImage from "resources/images/CarChargerImage.png";
import { useNavigate } from "react-router-dom";
import SignUpFormLayout from "../../components/SignUpFormLayout";
import TextField from "components/basic/textField";
import Checkbox from "components/basic/checkbox";
import GooglePlacesSearch from "components/basic/googlePlacesSearch";
import { Account, Vendor } from "../..";
import { useSnack } from "hooks/useSnack";
import { useFieldErrors } from "hooks/useFieldErrors";
import { validateBusinessInfo } from "./validation";

interface BusinessInfoFormProps {
  signUpData: Vendor;
  setSignUpData: React.Dispatch<React.SetStateAction<Vendor>>;
  // The business address is stored on the registering account
  account: Account;
  setAccount: React.Dispatch<React.SetStateAction<Account>>;
}

const BusinessInfoForm: React.FC<BusinessInfoFormProps> = ({
  signUpData,
  setSignUpData,
  account,
  setAccount,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const showSnack = useSnack();
  const { t } = useTranslation("auth");

  const { errors, validateAll, revalidate } =
    useFieldErrors(validateBusinessInfo);

  // What the validator sees: vendor fields plus the address off the account.
  const validationData = (vendor: Vendor, address: string) => ({
    ...vendor,
    address,
  });

  // Applies an edit and re-checks any error already on screen.
  const updateField = (patch: Partial<Vendor>) => {
    const next = { ...signUpData, ...patch };
    setSignUpData(next);
    revalidate(validationData(next, account.address));
  };

  const updateAddress = (patch: Partial<Account>) => {
    const next = { ...account, ...patch };
    setAccount(next);
    revalidate(validationData(signUpData, next.address));
  };

  const handleNext = () => {
    if (!validateAll(validationData(signUpData, account.address))) {
      showSnack(t("common.fix_errors"), "error");
      return;
    }

    navigate("?currentPage=privacyContactForm");
  };

  const handleChange = (name: string, value: string) =>
    updateField({ [name]: value } as Partial<Vendor>);

  const handleBusinessNameChange = (value: string) =>
    updateField({
      businessName: value,
      ...(signUpData.operatingAsSameAsLegal ? { operatingAs: value } : {}),
    });

  const handleSameAsLegalChange = (checked: boolean) =>
    updateField({
      operatingAsSameAsLegal: checked,
      operatingAs: checked ? signUpData.businessName : "",
    });

  return (
    <SignUpFormLayout
      imageSrc={CarChargerImage}
      backAction={{
        label: t("common.back"),
        color: "primary",
        onClick: () =>
          navigate(
            account.authProvider === "google"
              ? "?currentPage=addressForm"
              : "?currentPage=emailVerificationForm",
          ),
      }}
      primaryAction={{
        label: t("common.next"),
        color: "warning",
        onClick: handleNext,
      }}
    >
      <div>
        <Typography variant="body2" align="inherit">
          {t("business.name")}
        </Typography>

        <TextField
          name="businessName"
          value={signUpData.businessName}
          onChange={({ target: { value } }) => handleBusinessNameChange(value)}
          placeHolder={t("common.please_enter")}
          size="tiny"
          textAlign="right"
          error={errors.businessName ?? undefined}
        />
      </div>

      <div>
        <Checkbox
          isChecked={signUpData.operatingAsSameAsLegal}
          onChange={handleSameAsLegalChange}
          label={t("business.operating_same")}
        />
      </div>

      <div>
        <Typography variant="body2" align="inherit">
          {t("business.operating_as")}
        </Typography>

        <TextField
          placeHolder={t("common.please_enter")}
          name="operatingAs"
          size="tiny"
          textAlign="right"
          value={signUpData.operatingAs ?? ""}
          onChange={({ target: { name, value } }) => handleChange(name, value)}
          isDisabled={signUpData.operatingAsSameAsLegal}
          error={errors.operatingAs ?? undefined}
        />
      </div>

      <div>
        <Typography variant="body2" align="inherit">
          {t("business.address")}
        </Typography>

        <GooglePlacesSearch
          placeHolder={t("common.please_enter")}
          name="address"
          value={account.address}
          size="tiny"
          textAlign="right"
          style={{ marginBottom: theme.spacing(1) }}
          onInputChange={(text) =>
            updateAddress({
              address: text,
              latitude: null,
              longitude: null,
              placeId: null,
            })
          }
          onSelect={(place) =>
            updateAddress({
              address: place.address,
              latitude: place.latitude ?? null,
              longitude: place.longitude ?? null,
              placeId: place.placeId ?? null,
            })
          }
          error={errors.address ?? undefined}
        />

        <Checkbox
          isChecked={signUpData.mailingSameAsBusiness}
          onChange={(checked) =>
            updateField({
              mailingSameAsBusiness: checked,
              mailingAddress: null,
            })
          }
          label={t("business.mailing_same")}
        />
      </div>

      {!signUpData.mailingSameAsBusiness && (
        <div>
          <Typography variant="body2" align="inherit">
            {t("business.mailing")}
          </Typography>

          <GooglePlacesSearch
            placeHolder={t("common.please_enter")}
            name="mailingAddress"
            value={signUpData.mailingAddress ?? ""}
            size="tiny"
            textAlign="right"
            onInputChange={(text) => updateField({ mailingAddress: text })}
            onSelect={(place) => updateField({ mailingAddress: place.address })}
            error={errors.mailingAddress ?? undefined}
          />
        </div>
      )}
    </SignUpFormLayout>
  );
};

export default BusinessInfoForm;
